// frontend/src/modules/Purchase/pages/PurchaseQuickVoucher.js
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Spin, App, Typography, Row, Col } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";

import { getInstitutionApi } from "../../../../../apiservice/setting";
import { purchaseRecordnWithDetailsApi } from "../../../../../apiservice/purchase";

import ReportDom from "../../../../../components/common/ReportDom";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import PurchaseVoucherBody from "../../PurchaseVoucher/components/PurchaseVoucherBody";
import PurchaseVoucherBodyDesignA from "../../PurchaseVoucher/components/PurchaseVoucherBodyDesignA";
import { useShareDocument } from "../../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const PurchaseQuickVoucher = ({ authInfo }) => {
  const { id } = useParams(); // route: /purchase/:id
  const navigate = useNavigate();
  const { message } = App.useApp();
  
  const reportRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Purchase_Voucher_${voucher?.pur_voucher_no || "Voucher"}`,
  });

  // 🔹 Fetch Institution + Voucher details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [instRes, voucherRes] = await Promise.all([
          getInstitutionApi(),
          purchaseRecordnWithDetailsApi({ pur_id: id, from: "voucher" }),
        ]);
        setInstitution(instRes?.data || null);
        if (voucherRes?.data?.length > 0) {
          setVoucher(voucherRes.data[0]);
        } else {
          message.warning("No purchase voucher data found.");
        }
      } catch (err) {
        console.error("Purchase quick voucher load failed:", err);
        message.error("Failed to load voucher.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id, message]);

  useEffect(() => {
    if (shareOpen && supplierEmail) {
      setEmail(supplierEmail);
    }
  }, [shareOpen, supplierEmail, setEmail]);

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/purchase")}>Back</Button>
      </div>
    );

  return (
    <div className="voucher-page-wrapper" style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Buttons */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
        <Row gutter={8}>
          <Col>
            <ShareDocumentButton
              disabled={!voucher}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/purchase/${voucher.pur_id}`,
                  fileName: `Purchase_${
                    voucherNo || voucher.pur_voucher_no || "Voucher"
                  }.pdf`,
                  name: accName || "",
                  docNo:
                    voucherNo ||
                    voucher.pur_voucher_no ||
                    "Voucher",
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/purchase/${voucher.pur_id}`,
                  fileName: `Purchase_${
                    voucherNo || voucher.pur_voucher_no || "Voucher"
                  }.pdf`,
                  text: `Hello ${accName || ""}, Your Purchase ${
                    voucherNo ||
                    voucher.pur_voucher_no ||
                    "Voucher"
                  } is ready. Download here:`,
                })
              }
            />
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print Voucher
            </Button>
          </Col>
        </Row>
      </Col>
      </Row>

      {/* Voucher Display */}
      <Card
        className="shadow-md rounded-xl voucher-wrapper"
        style={{ padding: 24, background: "white" }}
      >
        <PurchaseVoucherBody
          pur_id={voucher.pur_id}
          institution={institution}
          printMode={false}
          setVoucherNo={setVoucherNo}
          setAccName={setAccName}
          setSupplierEmail={setSupplierEmail}
        />
      </Card>

      {/* Hidden Printable Report */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          mode={institution?.print_template === "design_a" ? "no-title" : undefined}
          title="Purchase Voucher"
          filters={{
            fromDate: voucher.created_date,
            toDate: voucher.created_date,
          }}
          renderTable={() =>
            institution?.print_template === "design_a" ? (
              <PurchaseVoucherBodyDesignA pur_id={voucher.pur_id} />
            ) : (
              <PurchaseVoucherBody
                pur_id={voucher.pur_id}
                institution={institution}
                printMode
              />
            )
          }
        />
      </div>
    </div>
  );
};

export default PurchaseQuickVoucher;
